import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { Music, Plus, Trash2 } from 'lucide-react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DragDropList } from '../planning/EditSelectionsModal/components/DragDropList/DragDropList';
import { ServiceSelection } from '../planning/types/planningTypes';
import apiClient from '../../utils/apiClient';
import ChurchChangeConfirmationModal from './ChurchChangeConfirmationModal';
import { useConfirmModal } from '../common/ConfirmModal';

interface TemplateDetail {
  template_id: number;
  season: string;
  liturgical_year: string;
  song_id: number;
  song_placement: string;
  song_title: string;
  composer_name: string;
  lyricist_name: string;
  tune_name: string;
  church_id?: number | null;
  church_name?: string;
  version_id?: number | null;
  version_title?: string;
}

// Using ServiceSelection from planning types for compatibility with DragDropList

interface Church {
  id: number;
  name: string;
}

interface EditTemplateModalProps {
  show: boolean;
  onHide: () => void;
  season: string;
  liturgicalYear: string;
  templateDetails: TemplateDetail[];
  onUpdate: () => void;
  cutoffCalendarId?: number;
  churchId?: number | null;
  churches?: Church[];
}

const EditTemplateModal: React.FC<EditTemplateModalProps> = ({
  show,
  onHide,
  season,
  liturgicalYear,
  templateDetails,
  onUpdate,
  cutoffCalendarId,
  churchId,
  churches = []
}) => {
  // Debug logging
  console.log('[EditTemplateModal] Props received:', {
    season,
    liturgicalYear,
    churchId,
    churchIdType: typeof churchId,
    templateDetailsCount: templateDetails.length,
    templateDetailsChurchId: templateDetails[0]?.church_id
  });

  const { confirm, ConfirmModal } = useConfirmModal();
  const [selections, setSelections] = useState<ServiceSelection[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [preserveExisting, setPreserveExisting] = useState(false);

  // Extract church info from template details if available, or use prop
  const initialChurchId = churchId !== undefined ? churchId : (templateDetails[0]?.church_id || null);
  console.log('[EditTemplateModal] initialChurchId:', initialChurchId);
  const [selectedChurchId, setSelectedChurchId] = useState<number | null>(initialChurchId);
  const [originalChurchId, setOriginalChurchId] = useState<number | null>(initialChurchId);
  const [showChurchChangeConfirm, setShowChurchChangeConfirm] = useState(false);
  const [affectedServiceCounts, setAffectedServiceCounts] = useState<{ old: number; new: number }>({ old: 0, new: 0 });

  // Update selectedChurchId and originalChurchId when template details change
  useEffect(() => {
    const newChurchId = churchId !== undefined ? churchId : (templateDetails[0]?.church_id || null);
    console.log('[EditTemplateModal] useEffect updating churchId:', {
      churchIdProp: churchId,
      templateDetailsChurchId: templateDetails[0]?.church_id,
      computedNewChurchId: newChurchId
    });
    setSelectedChurchId(newChurchId);
    setOriginalChurchId(newChurchId);
  }, [churchId, templateDetails]);

  useEffect(() => {
    if (templateDetails.length > 0) {
      const formattedSelections: ServiceSelection[] = templateDetails.map((detail, index) => ({
        service_selection_id: detail.template_id || index,
        service_id: 0, // Not used for templates
        song_id: detail.song_id,
        version_id: detail.version_id || null,
        song_title: detail.song_title,
        composer_name: detail.composer_name || '',
        lyricist_name: detail.lyricist_name || '',
        version_title: detail.version_title,
        placement: detail.song_placement,
        order_number: index + 1
      }));
      setSelections(formattedSelections);
    }
  }, [templateDetails]);

  const handleAddSelection = () => {
    const newSelection: ServiceSelection = {
      service_selection_id: Date.now(), // Temporary ID
      service_id: 0, // Not used for templates
      song_id: 0,
      version_id: null, // Will be set when user selects a song version
      song_title: '',
      composer_name: '',
      lyricist_name: '',
      version_title: undefined, // Will be set when user selects a song version
      placement: '',
      order_number: selections.length + 1
    };
    setSelections([...selections, newSelection]);
  };

  const handleDeleteSelection = (index: number) => {
    const newSelections = selections.filter((_, i) => i !== index);
    // Reorder the remaining selections
    const reorderedSelections = newSelections.map((sel, i) => ({
      ...sel,
      order_number: i + 1
    }));
    setSelections(reorderedSelections);
  };

  const handleSelectionsChange = (updatedSelections: ServiceSelection[]) => {
    // Reorder the selections and update order numbers
    const reorderedSelections = updatedSelections.map((sel, i) => ({
      ...sel,
      order_number: i + 1
    }));
    setSelections(reorderedSelections);
  };

  // Check if church assignment has changed
  const hasChurchChanged = () => {
    return selectedChurchId !== originalChurchId;
  };

  // Fetch service counts for both old and new churches
  const fetchServiceCounts = async () => {
    try {
      // Fetch count for old church
      const oldChurchParam = originalChurchId !== null && originalChurchId !== undefined
        ? `?churchId=${originalChurchId}`
        : '?churchId=';
      const oldResponse = await apiClient.get(
        `/service-music-templates/count-services/${encodeURIComponent(season)}/${encodeURIComponent(liturgicalYear)}${oldChurchParam}`
      );

      // Fetch count for new church
      const newChurchParam = selectedChurchId !== null && selectedChurchId !== undefined
        ? `?churchId=${selectedChurchId}`
        : '?churchId=';
      const newResponse = await apiClient.get(
        `/service-music-templates/count-services/${encodeURIComponent(season)}/${encodeURIComponent(liturgicalYear)}${newChurchParam}`
      );

      setAffectedServiceCounts({
        old: oldResponse.data.count || 0,
        new: newResponse.data.count || 0
      });

      return true;
    } catch (error) {
      console.error('Failed to fetch service counts:', error);
      return false;
    }
  };

  // Handle the actual save logic after confirmation (if needed)
  const performSave = async () => {
    try {
      setIsSubmitting(true);

      // Filter out any empty selections
      const validSelections = selections.filter(sel => sel.song_id && sel.placement);

      // Format for API
      const templateSongs = validSelections.map(sel => ({
        song_id: sel.song_id,
        song_placement: sel.placement,
        version_id: sel.version_id || null
      }));

      console.log('[EditTemplateModal] performSave called with:', {
        season,
        liturgicalYear,
        selectedChurchId,
        originalChurchId,
        hasChurchChanged: hasChurchChanged(),
        templateSongsCount: templateSongs.length,
        templateSongs
      });

      // If church has changed, we need special handling
      if (hasChurchChanged()) {
        // Step 1: Delete old template using ORIGINAL church_id
        const deleteUrl = `/service-music-templates/complete/${encodeURIComponent(season)}/${encodeURIComponent(liturgicalYear)}`;
        await apiClient.delete(deleteUrl);

        // Step 2: Create new template with NEW church_id
        await apiClient.post('/service-music-templates/template', {
          season,
          liturgicalYear,
          songs: templateSongs,
          cutoffCalendarId,
          churchId: selectedChurchId,
          preserveExisting
        });
      } else {
        // No church change - just update template songs
        await apiClient.post('/service-music-templates/template', {
          season,
          liturgicalYear,
          songs: templateSongs,
          cutoffCalendarId,
          churchId: selectedChurchId,
          preserveExisting
        });
      }

      onUpdate();
      onHide();
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // Check if church has changed
    if (hasChurchChanged()) {
      // Fetch service counts first
      const countsLoaded = await fetchServiceCounts();
      if (!countsLoaded) {
        alert('Failed to load service counts. Please try again.');
        return;
      }

      // Show confirmation dialog
      setShowChurchChangeConfirm(true);
    } else {
      // No church change - save directly
      await performSave();
    }
  };

  const handleConfirmChurchChange = async () => {
    setShowChurchChangeConfirm(false);
    await performSave();
  };

  const handleCancelChurchChange = () => {
    setShowChurchChangeConfirm(false);
  };

  const handleDeleteTemplate = () => {
    confirm({
      title: 'Delete Template',
      message: `Are you sure you want to delete this template?\n\nThis will remove:\n- The template definition\n- All template music from ALL services in ${season} ${liturgicalYear.replace('_', ' ')}\n\nThis action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          setIsDeleting(true);

          await apiClient.delete(`/service-music-templates/complete/${encodeURIComponent(season)}/${encodeURIComponent(liturgicalYear)}`);

          onUpdate();
          onHide();
        } catch (error) {
          console.error('Failed to delete template:', error);
          alert('Failed to delete template. Please try again.');
        } finally {
          setIsDeleting(false);
        }
      }
    });
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <Music size={24} className="me-2" />
          Edit Template: {season} {liturgicalYear.replace('_', ' ')}
          {selectedChurchId ? (
            <span className="text-muted"> - {churches.find(c => c.id === selectedChurchId)?.name || 'Unknown Church'}</span>
          ) : (
            <span className="text-muted"> - All Churches</span>
          )}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div className="mb-3">
          {/* Church Selection */}
          {churches.length > 0 && (
            <div className="mb-3">
              <label htmlFor="churchSelect" className="form-label">
                <strong>Church Assignment</strong>
              </label>
              <select
                id="churchSelect"
                className="form-select"
                value={selectedChurchId?.toString() || ''}
                onChange={(e) => setSelectedChurchId(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">All Churches</option>
                {churches.map((church) => (
                  <option key={church.id} value={church.id}>
                    {church.name}
                  </option>
                ))}
              </select>
              <small className="text-muted">
                Select which church this template applies to, or leave as "All Churches" to apply to all churches.
              </small>
            </div>
          )}

          {/* Show preserve existing option for Pentecost split templates */}
          {(season.includes('Pentecost - ')) && (
            <div className="alert alert-info mb-3">
              <h6 className="alert-heading mb-2">Template Application Options</h6>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="preserveExisting"
                  checked={preserveExisting}
                  onChange={(e) => setPreserveExisting(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="preserveExisting">
                  <strong>Preserve existing service music selections</strong>
                </label>
              </div>
              <small className="text-muted mt-1 d-block">
                {preserveExisting ? (
                  <>
                    ✅ Existing music will be kept. New template songs will be added alongside existing selections.
                    {season === 'Pentecost - Fall' ? ' This will only affect services after your selected cutoff date.' : ' This will only affect services up to your selected cutoff date.'}
                  </>
                ) : (
                  <>
                    ⚠️ Existing template music will be removed and replaced with this new template.
                    {season === 'Pentecost - Fall' ? ' This will only affect services after your selected cutoff date.' : ' This will only affect services up to your selected cutoff date.'}
                  </>
                )}
              </small>
            </div>
          )}
          
          <Button 
            variant="success" 
            size="sm" 
            onClick={handleAddSelection}
            className="mb-3"
          >
            <Plus size={16} className="me-1" />
            Add Song
          </Button>
          
          {selections.length === 0 ? (
            <div className="text-center text-muted py-4">
              No songs in this template. Click "Add Song" to start.
            </div>
          ) : (
            <DndProvider backend={HTML5Backend}>
              <DragDropList
                selections={selections}
                onChange={handleSelectionsChange}
                onDelete={handleDeleteSelection}
              />
            </DndProvider>
          )}
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <Button 
            variant="outline-danger" 
            onClick={handleDeleteTemplate}
            disabled={isSubmitting || isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 size={16} className="me-2" />
                Delete Template
              </>
            )}
          </Button>
          
          <div>
            <Button variant="secondary" onClick={onHide} disabled={isSubmitting || isDeleting} className="me-2">
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit}
              disabled={isSubmitting || isDeleting || selections.filter(s => s.song_id && s.placement).length === 0}
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Template'
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>

      {/* Church Change Confirmation Modal */}
      <ChurchChangeConfirmationModal
        show={showChurchChangeConfirm}
        onConfirm={handleConfirmChurchChange}
        onCancel={handleCancelChurchChange}
        oldChurchId={originalChurchId}
        newChurchId={selectedChurchId}
        churches={churches}
        season={season}
        liturgicalYear={liturgicalYear}
        oldServiceCount={affectedServiceCounts.old}
        newServiceCount={affectedServiceCounts.new}
      />

      <ConfirmModal />
    </Modal>
  );
};

export default EditTemplateModal;